import actorMigrations from "@-/dev.improve.haniverse.database.schemas.actor.migrations";
import { compareActorJournalToBundle } from "#actor-journal-head";

export type ReadActorRows = (sql: string) => Promise<Record<string, unknown>[]>;
export type ExecuteActorMigration = (sql: string, args?: (string | number)[]) => Promise<unknown>;

/** The migration journal every copy of the actor database carries. */
export const ACTOR_JOURNAL_TABLE_SQL =
  "CREATE TABLE IF NOT EXISTS __drizzle_migrations (id INTEGER PRIMARY KEY, hash TEXT NOT NULL, created_at NUMERIC, name TEXT, applied_at TEXT)";

const STATEMENT_BREAKPOINT = "--> statement-breakpoint";
const TAG_TIMESTAMP_LENGTH = 14;

/**
 * The database holds a migration this build does not have at that position, so nothing can be
 * applied on top of it. A linear, immutable history never produces this: the file was written by
 * another branch, or dates from before the history was squashed. Its owner starts it over.
 */
export class ActorForeignHistoryError extends Error {
  override readonly name = "ActorForeignHistoryError";
  readonly journal: readonly string[];

  // No parameter property: the dev launcher loads this file with Node's own type stripping.
  constructor(journal: readonly string[]) {
    super(
      `The study database holds migrations this build does not have at that position: ${journal.join(", ")}`,
    );
    this.journal = journal;
  }
}

/** The names in a database's migration journal, in order. A database without one has none. */
export async function readActorJournal(read: ReadActorRows): Promise<string[]> {
  const table = await read(
    "SELECT name FROM sqlite_master WHERE type = 'table' AND name = '__drizzle_migrations'",
  );
  if (table.length === 0) {
    return [];
  }
  const rows = await read("SELECT name FROM __drizzle_migrations ORDER BY name");
  return rows.map((row) => String(row["name"]));
}

/** When a migration was written, as drizzle records it: the timestamp its tag starts with. */
export function readActorMigrationCreatedAt(tag: string): number {
  const stamp = tag.slice(0, TAG_TIMESTAMP_LENGTH);
  const millis = Date.UTC(
    Number(stamp.slice(0, 4)),
    Number(stamp.slice(4, 6)) - 1,
    Number(stamp.slice(6, 8)),
    Number(stamp.slice(8, 10)),
    Number(stamp.slice(10, 12)),
    Number(stamp.slice(12, 14)),
  );
  return Number.isFinite(millis) ? millis : 0;
}

/** The statements of one migration file, in order. */
export function splitActorMigrationStatements(source: string): string[] {
  return source
    .split(STATEMENT_BREAKPOINT)
    .map((statement) => statement.trim())
    .filter((statement) => statement !== "");
}

/**
 * Apply every bundled migration the database lacks and record each in the journal. Returns the
 * tags it applied; none when the database is current or ahead of this build.
 *
 * The caller owns ONE write transaction around the whole call, so the database either reaches this
 * build's schema or stays exactly where it was. Nothing is repaired, skipped or stamped.
 */
export async function applyActorMigrations(
  read: ReadActorRows,
  execute: ExecuteActorMigration,
  migrations: Readonly<Record<string, string>> = actorMigrations.migrations,
): Promise<string[]> {
  const bundle = Object.keys(migrations).sort();
  const journal = await readActorJournal(read);
  const relation = compareActorJournalToBundle(journal, bundle);
  if (relation === "diverged") {
    throw new ActorForeignHistoryError(journal);
  }
  if (relation !== "behind") {
    return [];
  }
  await execute(ACTOR_JOURNAL_TABLE_SQL);
  const pending = bundle.slice(journal.length);
  for (const tag of pending) {
    const statements = splitActorMigrationStatements(migrations[tag] ?? "");
    if (statements.length === 0) {
      throw new Error(`Actor migration ${tag} has no statements`);
    }
    for (const statement of statements) {
      await execute(statement);
    }
    await execute(
      "INSERT INTO __drizzle_migrations (hash, created_at, name, applied_at) VALUES (?, ?, ?, ?)",
      ["", readActorMigrationCreatedAt(tag), tag, new Date().toISOString()],
    );
  }
  return pending;
}
